import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowRight, Sparkles, Upload, Package, Truck, MapPin } from "lucide-react";
import { QUANTITY_SHOWCASE } from "../data/pricing";

const CATEGORIES = [
  { id: "rolls", to: "/stickers-on-rolls", title: "Stickers on Rolls", body: "Ideal for high-volume labelling and fast application.", image: "/products/stickers-on-rolls-v2.webp", cta: "Shop Stickers on Rolls" },
  { id: "die-cut", to: "/die-cut-stickers", title: "Die-Cut Stickers", body: "Custom printed stickers in the shape you need.", image: "/products/custom-stickers.webp", cta: "Shop Die-Cut Stickers" },
  { id: "sheets", to: "/stickers-on-sheets", title: "Stickers on Sheets", body: "Multiple stickers printed together on convenient sheets.", image: "/products/stickers-on-sheets.webp", cta: "Shop Stickers on Sheets" },
];

function CategoryCard({ id, to, title, body, image, cta }) {
  return (
    <Link to={to} className="sf-card overflow-hidden flex flex-col group" data-testid={`sticker-category-${id}`}>
      <div className="aspect-[16/9] bg-[#F3EFEA] overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="p-6 flex flex-col gap-2 flex-1">
        <h2 className="font-display text-2xl lg:text-3xl font-extrabold leading-tight">{title}</h2>
        <p className="text-slate-600 flex-1">{body}</p>
        <div className="mt-2"><span className="btn-primary" data-testid={`sticker-category-${id}-cta`}>{cta} <ArrowRight size={16} /></span></div>
      </div>
    </Link>
  );
}

export default function Home() {
  return (
    <div className="App overflow-x-clip">
      <Header />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="hero-backdrop">
          <div className="hero-blob hero-blob--red" />
          <div className="hero-blob hero-blob--blue" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-8 pb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="eyebrow mb-3" data-testid="hero-eyebrow">Kwik Kopy Shellharbour · Printed Locally</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-[1] tracking-tight" data-testid="stickers-hero-headline">
              3 FINISH STYLES.{" "}
              <span className="relative inline-block">
                <span className="text-[#E4002B]">INFINITE</span> POSSIBILITIES.
                <span className="absolute left-0 right-0 -bottom-1.5 h-[5px] bg-[#E4002B] rounded-full" aria-hidden="true" />
              </span>
            </h1>
          </div>
          <p className="text-base text-slate-700 max-w-sm lg:text-right">
            Your artwork on rolls, die-cut to shape or on sheets. Printed locally in full colour.
          </p>
        </div>
      </section>

      {/* THREE CHOICE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-6 pb-16 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="sticker-categories">
        {CATEGORIES.map((c) => <CategoryCard key={c.id} {...c} />)}
      </section>

      {/* QUANTITY SHOWCASE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="eyebrow">From 50 to 100,000+ stickers</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">Any quantity. Any order.</h2>
          </div>
          <p className="text-slate-600 max-w-md">Whether you need a handful or a full production run, we've got you covered.</p>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
          {QUANTITY_SHOWCASE.map((q) => (
            <div key={q} className="sf-card !rounded-2xl p-4 text-center" data-testid={`qty-chip-${q}`}>
              <div className="font-display text-xl md:text-2xl font-black">{q.toLocaleString()}{q >= 100000 ? "+" : ""}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">stickers</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="eyebrow">Why Kwik Stickers</div>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-2 mb-10 max-w-xl">Premium stickers, zero drama.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {[
            { icon: Package, title: "Full die-cut", body: "Cleanly cut to the shape of your design." },
            { icon: Sparkles, title: "Bulk pricing", body: "From 50 to 100,000+ stickers." },
            { icon: Upload, title: "Rolls, die-cut or sheets", body: "Upload your own artwork in the format you need." },
            { icon: MapPin, title: "Local production", body: "Made in Australia." },
            { icon: Truck, title: "Delivery or pickup", body: "Australia-wide delivery or local pickup." },
          ].map((f) => (
            <div key={f.title} className="sf-card p-5">
              <f.icon size={22} className="text-[#E4002B] mb-3" />
              <div className="font-display font-bold text-lg">{f.title}</div>
              <p className="text-sm text-slate-600 mt-1">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MADE BY KWIK KOPY TRUST */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="sf-card p-8 lg:p-12 grid md:grid-cols-12 gap-6 items-center" data-testid="made-by-kwik-kopy">
          <div className="md:col-span-6 lg:col-span-7">
            <div className="eyebrow">Made by Kwik Kopy</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-2 leading-tight">Professional print. Serious production.</h2>
            <p className="mt-4 text-slate-600 max-w-xl">Your stickers are produced locally by the team at Kwik Kopy Shellharbour, using professional production equipment and commercial print finishing.</p>
          </div>
          <div className="md:col-span-6 lg:col-span-5 grid grid-cols-3 gap-2 lg:gap-3">
            <div className="rounded-2xl bg-[#F7F4F0] px-2 py-4 text-center border border-[#ECE7E0] min-w-0"><div className="font-display font-black text-2xl text-[#E4002B]">Local</div><div className="text-[11px] lg:text-xs font-semibold text-slate-500 mt-1 leading-tight">Shellharbour</div></div>
            <div className="rounded-2xl bg-[#F7F4F0] px-2 py-4 text-center border border-[#ECE7E0] min-w-0"><div className="font-display font-black text-2xl text-[#111315]">Pro</div><div className="text-[11px] lg:text-xs font-semibold text-slate-500 mt-1 leading-tight">Commercial print</div></div>
            <div className="rounded-2xl bg-[#F7F4F0] px-2 py-4 text-center border border-[#ECE7E0] min-w-0"><div className="font-display font-black text-2xl text-[#003DA5]">Fast</div><div className="text-[11px] lg:text-xs font-semibold text-slate-500 mt-1 leading-tight">Made to order</div></div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
